import type {
  ActionInput,
  ActionProgressInput,
  AssuranceTest,
  AssuranceTestInput,
  AuditEntry,
  Control,
  ControlInput,
  ControlObligation,
  ControlObligationInput,
  SamplingPlan,
  SamplingPlanInput,
  ObligationEscalation,
  ObligationEscalationInput,
  CustomFieldDefinition,
  CustomFieldDefinitionInput,
  CustomFieldValue,
  CustomFieldValueInput,
  EvidenceAttachment,
  EvidenceAttachmentInput,
  EvidenceInput,
  EvidenceLink,
  EvidencePackage,
  EvidencePackageInput,
  EvidencePackageItem,
  EvidenceRecord,
  ImportBatch,
  ImportBatchInput,
  ImportMappingProfile,
  ImportMappingProfileInput,
  ImportValidationRule,
  ImportValidationRuleInput,
  ImportRow,
  ImportRowResolutionInput,
  ExportBatch,
  ExportBatchInput,
  ExportReconciliationCommit,
  ExportReconciliationInput,
  ExportReconciliationRun,
  ExportReconciliationRow,
  ExportRow,
  ExportWorkspaceResponse,
  ReconciliationCommitInput,
  NotificationDispatchInput,
  NotificationDispatchResult,
  NotificationEvent,
  NotificationRule,
  QraModelRun,
  QraModelRunDetail,
  QraModelRunInput,
  QraModelRunUpdateInput,
  QraResultImportInput,
  ProgrammeItem,
  ProgrammeItemInput,
  ReportDeliverySendInput,
  ReportDeliveryEvent,
  ReportDeliveryJob,
  ReportDistributionList,
  ReportDistributionListInput,
  ReportDistributionRecipient,
  ReportPackBundle,
  ReportPackInput,
  ReportPackRun,
  ReportPackSection,
  ReportLayoutVariant,
  ReportLayoutVariantInput,
  ReportRun,
  ReportSchedule,
  ReportScheduleInput,
  ReportScheduleRun,
  ReportTemplate,
  ReportTemplateInput,
  ReportingPeriod,
  ReportingRow,
  ReportingTableResponse,
  RiskControl,
  RiskControlInput,
  RiskAppetiteAssessment,
  RiskAppetiteAssessmentInput,
  RiskAppetiteDetail,
  RiskEscalationEvent,
  RiskEscalationEventInput,
  RiskWorkshop,
  RiskWorkshopAttendee,
  RiskWorkshopDecision,
  RiskIndicator,
  RiskIndicatorInput,
  RiskIndicatorEvent,
  RiskIndicatorEventInput,
  RiskExposureStateEvidence,
  RiskExposureStateEvidenceInput,
  RiskQuantification,
  RiskQuantificationInput,
  RiskInput,
  RiskRecord,
  RiskTimelineEvent,
  SrsConnection,
  SrsConnectionInput,
  SrsSyncEvent,
  TenantPayload,
  TenantSettings,
  TaxonomyItem,
  TaxonomyItemInput,
  TenantUser,
  UserInput,
  PackageAccessGrant,
  PackageAccessGrantInput,
  TenantPackage,
  RetentionPolicy,
  RetentionPolicyInput,
  SecurityControl,
  SecurityControlInput,
  BackupManifest,
  BackupManifestInput,
  ComplianceWorkspaceResponse,
  IntegrationSetting,
  IntegrationSettingInput,
  IntegrationWorkspaceResponse,
  ScheduleImportBatch,
  ScheduleActivity,
  ScheduleImportInput,
  ScheduleWorkspaceResponse,
  CostImportBatch,
  CostLine,
  CostRiskReconciliation,
  CostImportInput,
  CostWorkspaceResponse,
  UserSession,
  WorkflowDefinition,
  WorkflowDefinitionInput,
  WorkflowEvent,
  WorkflowInstance,
  WorkflowTransitionInput,
} from "./types";

const FEEDBACK_ENDPOINT = "/api/feedback";

async function request<T>(url: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    credentials: "same-origin",
    headers: {
      "content-type": "application/json",
      ...(init.headers || {}),
    },
    ...init,
  });

  if (!response.ok) {
    let message = response.statusText;
    try {
      const payload = (await response.json()) as { error?: string };
      message = payload.error || message;
    } catch {
      // Keep the status text.
    }
    throw new Error(message);
  }

  return (await response.json()) as T;
}

export async function getSession() {
  return request<UserSession>("/api/session");
}

export async function startLogin(email: string) {
  return request<{ ok: true; emailSent: boolean; devCode?: string }>("/api/auth/start", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export async function verifyLogin(email: string, code: string) {
  return request<{ ok: true }>("/api/auth/verify", {
    method: "POST",
    body: JSON.stringify({ email, code }),
  });
}

export async function logout() {
  return request<{ ok: true }>("/api/auth/logout", { method: "POST" });
}

export async function loadTenant() {
  return request<TenantPayload>("/api/tenant");
}

export async function saveTenantSettings(settings: TenantSettings, categoryRenames: Array<{ from: string; to: string }> = []) {
  return request<TenantPayload>("/api/tenant", {
    method: "PUT",
    body: JSON.stringify({ ...settings, categoryRenames }),
  });
}

export async function loadRisks() {
  return request<{ risks: RiskRecord[] }>("/api/risks");
}

export async function loadProgrammeItems() {
  return request<{ items: ProgrammeItem[] }>("/api/programme-items");
}

export async function createProgrammeItem(input: ProgrammeItemInput) {
  return request<{ item: ProgrammeItem }>("/api/programme-items", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateProgrammeItem(code: string, input: ProgrammeItemInput) {
  return request<{ item: ProgrammeItem }>(`/api/programme-items/${encodeURIComponent(code)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function retireProgrammeItem(code: string) {
  return request<{ item: ProgrammeItem }>(`/api/programme-items/${encodeURIComponent(code)}`, { method: "DELETE" });
}

export async function loadImports(importBatchId = "") {
  const suffix = importBatchId ? `?importBatchId=${encodeURIComponent(importBatchId)}` : "";
  return request<{ batches: ImportBatch[]; rows: ImportRow[] }>(`/api/imports${suffix}`);
}

export async function processImport(input: ImportBatchInput) {
  return request<{ batch: ImportBatch; rows: ImportRow[] }>("/api/imports", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function loadImportMappingProfiles(targetType = "") {
  const suffix = targetType ? `?targetType=${encodeURIComponent(targetType)}` : "";
  return request<{ profiles: ImportMappingProfile[] }>(`/api/imports/mapping-profiles${suffix}`);
}

export async function createImportMappingProfile(input: ImportMappingProfileInput) {
  return request<{ profile: ImportMappingProfile }>("/api/imports/mapping-profiles", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateImportMappingProfile(id: string, input: ImportMappingProfileInput) {
  return request<{ profile: ImportMappingProfile }>(`/api/imports/mapping-profiles/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function retireImportMappingProfile(id: string) {
  return request<{ profile: ImportMappingProfile }>(`/api/imports/mapping-profiles/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export async function loadImportValidationRules(targetType = "") {
  const suffix = targetType ? `?targetType=${encodeURIComponent(targetType)}` : "";
  return request<{ rules: ImportValidationRule[] }>(`/api/imports/validation-rules${suffix}`);
}

export async function createImportValidationRule(input: ImportValidationRuleInput) {
  return request<{ rule: ImportValidationRule }>("/api/imports/validation-rules", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateImportValidationRule(id: string, input: ImportValidationRuleInput) {
  return request<{ rule: ImportValidationRule }>(`/api/imports/validation-rules/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function retireImportValidationRule(id: string) {
  return request<{ rule: ImportValidationRule }>(`/api/imports/validation-rules/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export async function resolveImportRow(id: string, input: ImportRowResolutionInput) {
  return request<{ row: ImportRow }>(`/api/imports/rows/${encodeURIComponent(id)}/resolution`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function loadExports(params: { exportBatchId?: string; reconciliationRunId?: string } = {}) {
  const search = new URLSearchParams();
  if (params.exportBatchId) search.set("exportBatchId", params.exportBatchId);
  if (params.reconciliationRunId) search.set("reconciliationRunId", params.reconciliationRunId);
  const suffix = search.toString() ? `?${search.toString()}` : "";
  return request<ExportWorkspaceResponse>(`/api/imports/exports${suffix}`);
}

export async function createExportBatch(input: ExportBatchInput) {
  return request<{ batch: ExportBatch; rows: ExportRow[] }>("/api/imports/exports", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function loadExportRows(exportBatchId: string) {
  return request<{ rows: ExportRow[] }>(`/api/imports/exports/${encodeURIComponent(exportBatchId)}/rows`);
}

export async function reconcileExportBatch(exportBatchId: string, input: ExportReconciliationInput) {
  return request<{ run: ExportReconciliationRun; rows: ExportReconciliationRow[]; batch: ExportBatch }>(
    `/api/imports/exports/${encodeURIComponent(exportBatchId)}/reconcile`,
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
}

export async function commitReconciliationRun(reconciliationRunId: string, input: ReconciliationCommitInput) {
  return request<{
    run: ExportReconciliationRun;
    rows: ExportReconciliationRow[];
    commits: ExportReconciliationCommit[];
    committed: number;
    skipped: number;
    failed: number;
  }>(`/api/imports/reconciliations/${encodeURIComponent(reconciliationRunId)}/commit`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function downloadExportBatchCsv(exportBatchId: string) {
  const response = await fetch(`/api/imports/exports/${encodeURIComponent(exportBatchId)}/csv`, {
    credentials: "same-origin",
  });
  if (!response.ok) {
    let message = response.statusText;
    try {
      const payload = (await response.json()) as { error?: string };
      message = payload.error || message;
    } catch {
      // Keep the status text.
    }
    throw new Error(message);
  }
  const disposition = response.headers.get("content-disposition") || "";
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const fileName = match ? match[1] : `${exportBatchId}.csv`;
  const text = await response.text();
  return { fileName, text };
}

export async function loadEvidence(evidenceId = "") {
  const suffix = evidenceId ? `?evidenceId=${encodeURIComponent(evidenceId)}` : "";
  return request<{ items: EvidenceRecord[]; links: EvidenceLink[] }>(`/api/evidence${suffix}`);
}

export async function createEvidence(input: EvidenceInput) {
  return request<{ item: EvidenceRecord; links: EvidenceLink[] }>("/api/evidence", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateEvidence(id: string, input: EvidenceInput) {
  return request<{ item: EvidenceRecord; links: EvidenceLink[] }>(`/api/evidence/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function archiveEvidence(id: string) {
  return request<{ item: EvidenceRecord; links: EvidenceLink[] }>(`/api/evidence/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export async function loadEvidenceAttachments(evidenceId = "") {
  const suffix = evidenceId ? `?evidenceId=${encodeURIComponent(evidenceId)}` : "";
  return request<{ attachments: EvidenceAttachment[] }>(`/api/evidence/attachments${suffix}`);
}

export async function createEvidenceAttachment(evidenceId: string, input: EvidenceAttachmentInput) {
  return request<{ attachment: EvidenceAttachment }>(`/api/evidence/${encodeURIComponent(evidenceId)}/attachments`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function archiveEvidenceAttachment(id: string) {
  return request<{ attachment: EvidenceAttachment }>(`/api/evidence/attachments/${encodeURIComponent(id)}`, { method: "DELETE" });
}

// Upload native bytes for an attachment (raw binary body, not JSON) into R2-backed storage.
export async function uploadAttachmentBytes(id: string, file: File) {
  const response = await fetch(`/api/evidence/attachments/${encodeURIComponent(id)}/bytes`, {
    method: "PUT",
    credentials: "same-origin",
    headers: { "content-type": file.type || "application/octet-stream" },
    body: file,
  });
  if (!response.ok) {
    let message = response.statusText;
    try {
      message = ((await response.json()) as { error?: string }).error || message;
    } catch {
      /* keep status text */
    }
    throw new Error(message);
  }
  return (await response.json()) as { attachment: EvidenceAttachment };
}

export async function loadEvidencePackages(packageId = "") {
  const suffix = packageId ? `?packageId=${encodeURIComponent(packageId)}` : "";
  return request<{ packages: EvidencePackage[]; items: EvidencePackageItem[] }>(`/api/evidence/packages${suffix}`);
}

export async function createEvidencePackage(input: EvidencePackageInput) {
  return request<{ package: EvidencePackage; items: EvidencePackageItem[] }>("/api/evidence/packages", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function sealEvidencePackage(id: string) {
  return request<{ package: EvidencePackage; items: EvidencePackageItem[] }>(`/api/evidence/packages/${encodeURIComponent(id)}/seal`, { method: "POST" });
}

export async function loadCustomFieldDefinitions(entityType = "") {
  const suffix = entityType ? `?entityType=${encodeURIComponent(entityType)}` : "";
  return request<{ items: CustomFieldDefinition[] }>(`/api/config/custom-fields${suffix}`);
}

export async function createCustomFieldDefinition(input: CustomFieldDefinitionInput) {
  return request<{ item: CustomFieldDefinition }>("/api/config/custom-fields", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateCustomFieldDefinition(id: string, input: CustomFieldDefinitionInput) {
  return request<{ item: CustomFieldDefinition }>(`/api/config/custom-fields/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function retireCustomFieldDefinition(id: string) {
  return request<{ item: CustomFieldDefinition }>(`/api/config/custom-fields/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export async function loadWorkflowDefinitions(entityType = "") {
  const suffix = entityType ? `?entityType=${encodeURIComponent(entityType)}` : "";
  return request<{ items: WorkflowDefinition[] }>(`/api/config/workflows${suffix}`);
}

export async function createWorkflowDefinition(input: WorkflowDefinitionInput) {
  return request<{ item: WorkflowDefinition }>("/api/config/workflows", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateWorkflowDefinition(id: string, input: WorkflowDefinitionInput) {
  return request<{ item: WorkflowDefinition }>(`/api/config/workflows/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function retireWorkflowDefinition(id: string) {
  return request<{ item: WorkflowDefinition }>(`/api/config/workflows/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export async function loadCustomFieldValues(filters: { entityType?: string; entityId?: string } = {}) {
  const params = new URLSearchParams();
  if (filters.entityType) params.set("entityType", filters.entityType);
  if (filters.entityId) params.set("entityId", filters.entityId);
  return request<{ items: CustomFieldValue[] }>(`/api/config/custom-field-values${params.toString() ? `?${params}` : ""}`);
}

export async function upsertCustomFieldValue(input: CustomFieldValueInput) {
  return request<{ item: CustomFieldValue }>("/api/config/custom-field-values", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function loadWorkflows(filters: { entityType?: string; entityId?: string; workflowInstanceId?: string } = {}) {
  const params = new URLSearchParams();
  if (filters.entityType) params.set("entityType", filters.entityType);
  if (filters.entityId) params.set("entityId", filters.entityId);
  if (filters.workflowInstanceId) params.set("workflowInstanceId", filters.workflowInstanceId);
  return request<{ definitions: WorkflowDefinition[]; instances: WorkflowInstance[]; events: WorkflowEvent[] }>(`/api/workflows${params.toString() ? `?${params}` : ""}`);
}

export async function transitionWorkflow(id: string, input: WorkflowTransitionInput) {
  return request<{ item: WorkflowInstance; events: WorkflowEvent[] }>(`/api/workflows/${encodeURIComponent(id)}/transition`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function createRisk(input: RiskInput) {
  return request<{ risk: RiskRecord }>("/api/risks", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateRisk(id: string, input: RiskInput) {
  return request<{ risk: RiskRecord }>(`/api/risks/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function deleteRisk(id: string) {
  return request<{ ok: true }>(`/api/risks/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export async function loadRiskAppetite(id: string) {
  return request<RiskAppetiteDetail>(`/api/risks/${encodeURIComponent(id)}/appetite`);
}

export async function saveRiskAppetiteAssessment(id: string, input: RiskAppetiteAssessmentInput) {
  return request<RiskAppetiteDetail>(`/api/risks/${encodeURIComponent(id)}/appetite`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function recordRiskEscalationEvent(id: string, input: RiskEscalationEventInput) {
  return request<RiskAppetiteDetail>(`/api/risks/${encodeURIComponent(id)}/appetite/escalations`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function loadRiskQuantification(id: string) {
  return request<{ quantification: RiskQuantification }>(`/api/risks/${encodeURIComponent(id)}/quantification`);
}

export async function loadRiskTimeline(id: string) {
  return request<{ riskId: string; events: RiskTimelineEvent[] }>(`/api/risks/${encodeURIComponent(id)}/timeline`);
}

export async function loadNotifications(status = "") {
  const suffix = status ? `?status=${encodeURIComponent(status)}` : "";
  return request<{ rules: NotificationRule[]; events: NotificationEvent[] }>(`/api/notifications${suffix}`);
}

export async function dispatchDueNotifications(input: NotificationDispatchInput) {
  return request<NotificationDispatchResult>("/api/notifications/dispatch-due", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function acknowledgeNotificationEvent(id: string, note = "") {
  return request<{ event: NotificationEvent }>(`/api/notifications/${encodeURIComponent(id)}/acknowledge`, {
    method: "POST",
    body: JSON.stringify({ note }),
  });
}

export async function dismissNotificationEvent(id: string, note = "") {
  return request<{ event: NotificationEvent }>(`/api/notifications/${encodeURIComponent(id)}/dismiss`, {
    method: "POST",
    body: JSON.stringify({ note }),
  });
}

export async function loadRiskExposureStateEvidence(id: string) {
  return request<{ items: RiskExposureStateEvidence[] }>(`/api/risks/${encodeURIComponent(id)}/quantification/evidence`);
}

export async function saveRiskExposureStateEvidence(id: string, items: RiskExposureStateEvidenceInput[]) {
  return request<{ items: RiskExposureStateEvidence[] }>(`/api/risks/${encodeURIComponent(id)}/quantification/evidence`, {
    method: "PUT",
    body: JSON.stringify({ items }),
  });
}

export async function saveRiskQuantification(id: string, input: RiskQuantificationInput) {
  return request<{ quantification: RiskQuantification }>(`/api/risks/${encodeURIComponent(id)}/quantification`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function submitRiskQuantification(id: string, note: string) {
  return request<{ quantification: RiskQuantification }>(`/api/risks/${encodeURIComponent(id)}/quantification/submit`, {
    method: "POST",
    body: JSON.stringify({ note }),
  });
}

export async function approveRiskQuantification(id: string, note: string, attestationAccepted: boolean, attestationStatement: string) {
  return request<{ quantification: RiskQuantification }>(`/api/risks/${encodeURIComponent(id)}/quantification/approve`, {
    method: "POST",
    body: JSON.stringify({ note, attestationAccepted, attestationStatement }),
  });
}

export async function challengeRiskQuantification(id: string, note: string) {
  return request<{ quantification: RiskQuantification }>(`/api/risks/${encodeURIComponent(id)}/quantification/challenge`, {
    method: "POST",
    body: JSON.stringify({ note }),
  });
}

export async function rejectRiskQuantification(id: string, note: string) {
  return request<{ quantification: RiskQuantification }>(`/api/risks/${encodeURIComponent(id)}/quantification/reject`, {
    method: "POST",
    body: JSON.stringify({ note }),
  });
}

export async function createAction(riskId: string, input: ActionInput) {
  return request<{ risk: RiskRecord }>(`/api/risks/${encodeURIComponent(riskId)}/actions`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateAction(riskId: string, actionId: string, input: ActionInput) {
  return request<{ risk: RiskRecord }>(`/api/risks/${encodeURIComponent(riskId)}/actions/${encodeURIComponent(actionId)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function deleteAction(riskId: string, actionId: string) {
  return request<{ risk: RiskRecord }>(`/api/risks/${encodeURIComponent(riskId)}/actions/${encodeURIComponent(actionId)}`, {
    method: "DELETE",
  });
}

export async function recordActionProgress(riskId: string, actionId: string, input: ActionProgressInput) {
  return request<{ risk: RiskRecord; event: { id: string } }>(`/api/risks/${encodeURIComponent(riskId)}/actions/${encodeURIComponent(actionId)}/progress`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function sendFeedback(payload: Record<string, string>) {
  return request<{ ok: true; emailSent: boolean }>(FEEDBACK_ENDPOINT, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function loadUsers() {
  return request<{ users: TenantUser[] }>("/api/users");
}

export async function createUser(input: UserInput) {
  return request<{ users: TenantUser[] }>("/api/users", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateUser(id: string, input: UserInput) {
  return request<{ users: TenantUser[] }>(`/api/users/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function deactivateUser(id: string) {
  return request<{ users: TenantUser[] }>(`/api/users/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export async function loadPackageGrants() {
  return request<{ grants: PackageAccessGrant[]; packages: TenantPackage[]; users: TenantUser[] }>("/api/admin/package-grants");
}

export async function createPackageGrant(input: PackageAccessGrantInput) {
  return request<{ grant: PackageAccessGrant; grants: PackageAccessGrant[] }>("/api/admin/package-grants", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function revokePackageGrant(id: string) {
  return request<{ ok: boolean; grants: PackageAccessGrant[] }>(`/api/admin/package-grants/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export async function loadCompliance() {
  return request<ComplianceWorkspaceResponse>("/api/admin/compliance");
}

export async function loadIntegrations() {
  return request<IntegrationWorkspaceResponse>("/api/admin/integrations");
}

export async function saveIntegration(key: string, input: IntegrationSettingInput) {
  return request<{ setting: IntegrationSetting; settings: IntegrationSetting[] }>(`/api/admin/integrations/${encodeURIComponent(key)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function verifyIntegration(key: string) {
  return request<{ ok: boolean; missing: string[]; note: string; setting: IntegrationSetting; settings: IntegrationSetting[] }>(`/api/admin/integrations/${encodeURIComponent(key)}/verify`, {
    method: "POST",
  });
}

export async function createRetentionPolicy(input: RetentionPolicyInput) {
  return request<{ policy: RetentionPolicy; policies: RetentionPolicy[] }>("/api/admin/compliance/retention-policies", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function retireRetentionPolicy(id: string) {
  return request<{ policies: RetentionPolicy[] }>(`/api/admin/compliance/retention-policies/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export async function createSecurityControl(input: SecurityControlInput) {
  return request<{ control: SecurityControl; controls: SecurityControl[] }>("/api/admin/compliance/security-controls", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function recordBackupManifest(input: BackupManifestInput) {
  return request<{ manifest: BackupManifest; manifests: BackupManifest[] }>("/api/admin/compliance/backup-manifests", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function loadSchedule() {
  return request<ScheduleWorkspaceResponse>("/api/imports/schedule");
}

export async function processScheduleImport(input: ScheduleImportInput) {
  return request<{ batch: ScheduleImportBatch; rows: unknown[]; activities: ScheduleActivity[] }>("/api/imports/schedule", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function loadCost() {
  return request<CostWorkspaceResponse>("/api/imports/cost");
}

export async function processCostImport(input: CostImportInput) {
  return request<{ batch: CostImportBatch; rows: unknown[]; lines: CostLine[]; reconciliation: CostRiskReconciliation[] }>("/api/imports/cost", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function loadAudit(limit = 100) {
  return request<{ audit: AuditEntry[] }>(`/api/audit?limit=${encodeURIComponent(String(limit))}`);
}

export async function loadReportingPeriods() {
  return request<ReportingTableResponse<ReportingPeriod>>("/api/reporting/periods");
}

export async function createReportingPeriod(input: { name: string; periodStart: string; periodEnd: string; notes: string }) {
  return request<{ period: ReportingPeriod }>("/api/reporting/periods", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function snapshotReportingPeriod(id: string) {
  return request<{ period: ReportingPeriod; snapshotRowCount: number; rows: ReportingRow[] }>(`/api/reporting/periods/${encodeURIComponent(id)}/snapshot`, {
    method: "POST",
  });
}

export async function closeReportingPeriod(id: string) {
  return request<{ period: ReportingPeriod; snapshotRowCount: number }>(`/api/reporting/periods/${encodeURIComponent(id)}/close`, {
    method: "POST",
  });
}

export async function loadQraReadiness() {
  return request<ReportingTableResponse>("/api/reporting/qra-readiness");
}

export async function loadQraExport(mode: "ready" | "candidate" | "all" = "ready") {
  return request<ReportingTableResponse>(`/api/reporting/qra-export?mode=${encodeURIComponent(mode)}`);
}

export async function loadQraExposureStates() {
  return request<ReportingTableResponse>("/api/reporting/qra-exposure-states");
}

export async function loadRiskAppetiteAssessments() {
  return request<ReportingTableResponse<RiskAppetiteAssessment>>("/api/reporting/risk-appetite-assessments");
}

export async function loadRiskEscalationEvents() {
  return request<ReportingTableResponse<RiskEscalationEvent>>("/api/reporting/risk-escalation-events");
}

export async function loadRiskWorkshops() {
  return request<ReportingTableResponse<RiskWorkshop>>("/api/reporting/risk-workshops");
}

export async function loadRiskWorkshopAttendees() {
  return request<ReportingTableResponse<RiskWorkshopAttendee>>("/api/reporting/risk-workshop-attendees");
}

export async function loadRiskWorkshopDecisions() {
  return request<ReportingTableResponse<RiskWorkshopDecision>>("/api/reporting/risk-workshop-decisions");
}

export async function loadRiskIndicators() {
  return request<ReportingTableResponse<RiskIndicator>>("/api/reporting/risk-indicators");
}

export async function loadRiskIndicatorEvents() {
  return request<ReportingTableResponse<RiskIndicatorEvent>>("/api/reporting/risk-indicator-events");
}

export async function loadManagedRiskIndicators(riskId = "") {
  const suffix = riskId ? `?riskId=${encodeURIComponent(riskId)}` : "";
  return request<{ items: RiskIndicator[] }>(`/api/risk-indicators${suffix}`);
}

export async function createRiskIndicator(input: RiskIndicatorInput) {
  return request<{ item: RiskIndicator }>("/api/risk-indicators", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateRiskIndicator(id: string, input: RiskIndicatorInput) {
  return request<{ item: RiskIndicator }>(`/api/risk-indicators/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function retireRiskIndicator(id: string) {
  return request<{ item: RiskIndicator }>(`/api/risk-indicators/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export async function recordRiskIndicatorEvent(id: string, input: RiskIndicatorEventInput) {
  return request<{ item: RiskIndicatorEvent; indicator: RiskIndicator }>(`/api/risk-indicators/${encodeURIComponent(id)}/events`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function loadReportRuns() {
  return request<ReportingTableResponse<ReportRun>>("/api/reporting/report-runs");
}

export async function createReportRun(input: { reportType: string; reportTemplateId?: string; reportingPeriodId?: string; format: string; filters: Record<string, unknown> }) {
  return request<{ reportRun: ReportRun }>("/api/reporting/report-runs", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function loadReportTemplates() {
  return request<ReportingTableResponse<ReportTemplate>>("/api/reporting/templates");
}

export async function loadReportLayoutVariants() {
  return request<ReportingTableResponse<ReportLayoutVariant>>("/api/reporting/layout-variants");
}

export async function loadReportDistributionLists() {
  return request<ReportingTableResponse<ReportDistributionList>>("/api/reporting/distribution-lists");
}

export async function loadReportDistributionRecipients(distributionListId = "") {
  const suffix = distributionListId ? `?distributionListId=${encodeURIComponent(distributionListId)}` : "";
  return request<ReportingTableResponse<ReportDistributionRecipient>>(`/api/reporting/distribution-recipients${suffix}`);
}

export async function createReportDistributionList(input: ReportDistributionListInput) {
  return request<{ distributionList: ReportDistributionList }>("/api/reporting/distribution-lists", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateReportDistributionList(id: string, input: ReportDistributionListInput) {
  return request<{ distributionList: ReportDistributionList }>(`/api/reporting/distribution-lists/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function retireReportDistributionList(id: string) {
  return request<{ distributionList: ReportDistributionList }>(`/api/reporting/distribution-lists/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export async function loadReportSchedules() {
  return request<ReportingTableResponse<ReportSchedule>>("/api/reporting/schedules");
}

export async function loadReportScheduleRuns(reportScheduleId = "") {
  const suffix = reportScheduleId ? `?reportScheduleId=${encodeURIComponent(reportScheduleId)}` : "";
  return request<ReportingTableResponse<ReportScheduleRun>>(`/api/reporting/schedule-runs${suffix}`);
}

export async function loadReportDeliveryJobs(filters: { reportRunId?: string; reportScheduleRunId?: string } = {}) {
  const params = new URLSearchParams();
  if (filters.reportRunId) params.set("reportRunId", filters.reportRunId);
  if (filters.reportScheduleRunId) params.set("reportScheduleRunId", filters.reportScheduleRunId);
  return request<ReportingTableResponse<ReportDeliveryJob>>(`/api/reporting/delivery-jobs${params.toString() ? `?${params}` : ""}`);
}

export async function loadReportDeliveryEvents(reportDeliveryJobId = "") {
  const suffix = reportDeliveryJobId ? `?reportDeliveryJobId=${encodeURIComponent(reportDeliveryJobId)}` : "";
  return request<ReportingTableResponse<ReportDeliveryEvent>>(`/api/reporting/delivery-events${suffix}`);
}

export async function sendReportDeliveryJob(id: string, input: ReportDeliverySendInput) {
  return request<{ deliveryJob: ReportDeliveryJob; deliveryEvents: ReportDeliveryEvent[] }>(`/api/reporting/delivery-jobs/${encodeURIComponent(id)}/send`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function createReportSchedule(input: ReportScheduleInput) {
  return request<{ schedule: ReportSchedule }>("/api/reporting/schedules", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateReportSchedule(id: string, input: ReportScheduleInput) {
  return request<{ schedule: ReportSchedule }>(`/api/reporting/schedules/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function retireReportSchedule(id: string) {
  return request<{ schedule: ReportSchedule }>(`/api/reporting/schedules/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export async function runReportSchedule(id: string, input: { reportingPeriodId?: string; title?: string; issueNote: string; overrideIssue: boolean }) {
  return request<ReportPackBundle & { schedule: ReportSchedule; scheduleRun: ReportScheduleRun; deliveryJob: ReportDeliveryJob | null }>(`/api/reporting/schedules/${encodeURIComponent(id)}/run`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function dispatchDueReportSchedules(input: { dueDate: string; limit: number; dryRun: boolean; issueNote: string; overrideIssue: boolean }) {
  return request<{ dueDate: string; dryRun: boolean; dueCount: number; processedCount: number; results: ReportingRow[] }>("/api/reporting/schedules/dispatch-due", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function createReportLayoutVariant(input: ReportLayoutVariantInput) {
  return request<{ layoutVariant: ReportLayoutVariant }>("/api/reporting/layout-variants", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateReportLayoutVariant(id: string, input: ReportLayoutVariantInput) {
  return request<{ layoutVariant: ReportLayoutVariant }>(`/api/reporting/layout-variants/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function retireReportLayoutVariant(id: string) {
  return request<{ layoutVariant: ReportLayoutVariant }>(`/api/reporting/layout-variants/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export async function createReportTemplate(input: ReportTemplateInput) {
  return request<{ template: ReportTemplate }>("/api/reporting/templates", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateReportTemplate(id: string, input: ReportTemplateInput) {
  return request<{ template: ReportTemplate }>(`/api/reporting/templates/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function deactivateReportTemplate(id: string) {
  return request<{ template: ReportTemplate }>(`/api/reporting/templates/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export async function loadReportPacks() {
  return request<ReportingTableResponse<ReportPackRun>>("/api/reporting/report-packs");
}

export async function generateReportPack(input: ReportPackInput) {
  return request<ReportPackBundle>("/api/reporting/report-packs", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function loadReportPack(id: string) {
  return request<ReportPackBundle>(`/api/reporting/report-packs/${encodeURIComponent(id)}`);
}

export async function issueReportPack(id: string, input: { issueNote: string; overrideIssue: boolean }) {
  return request<ReportPackBundle>(`/api/reporting/report-packs/${encodeURIComponent(id)}/issue`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function loadReportPackBriefing(id: string) {
  return request<ReportingRow>(`/api/reporting/report-packs/${encodeURIComponent(id)}/briefing`);
}

export async function loadReportPackSections(reportRunId = "") {
  const suffix = reportRunId ? `?reportRunId=${encodeURIComponent(reportRunId)}` : "";
  return request<ReportingTableResponse<ReportPackSection>>(`/api/reporting/report-pack-sections${suffix}`);
}

export async function loadQraModelRuns() {
  return request<{ items: QraModelRun[] }>("/api/srs/model-runs");
}

export async function loadQraModelRun(id: string) {
  return request<QraModelRunDetail>(`/api/srs/model-runs/${encodeURIComponent(id)}`);
}

export async function createQraModelRun(input: QraModelRunInput) {
  return request<QraModelRunDetail>("/api/srs/model-runs", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateQraModelRun(id: string, input: QraModelRunUpdateInput) {
  return request<QraModelRunDetail>(`/api/srs/model-runs/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function supersedeQraModelRun(id: string) {
  return request<QraModelRunDetail>(`/api/srs/model-runs/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export async function importQraModelRunResults(id: string, input: QraResultImportInput) {
  return request<QraModelRunDetail>(`/api/srs/model-runs/${encodeURIComponent(id)}/results`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function loadSrsConnections() {
  return request<{ items: SrsConnection[] }>("/api/srs/connections");
}

export async function createSrsConnection(input: SrsConnectionInput) {
  return request<{ connection: SrsConnection }>("/api/srs/connections", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateSrsConnection(id: string, input: SrsConnectionInput) {
  return request<{ connection: SrsConnection }>(`/api/srs/connections/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function runSrsHealthCheck(id: string) {
  return request<{ connection: SrsConnection; event: SrsSyncEvent }>(`/api/srs/connections/${encodeURIComponent(id)}/health-check`, {
    method: "POST",
  });
}

export async function loadSrsSyncEvents(filters: { modelRunId?: string; connectionId?: string } = {}) {
  const params = new URLSearchParams();
  if (filters.modelRunId) params.set("modelRunId", filters.modelRunId);
  if (filters.connectionId) params.set("connectionId", filters.connectionId);
  return request<{ items: SrsSyncEvent[] }>(`/api/srs/sync-events${params.toString() ? `?${params}` : ""}`);
}

export async function pushQraModelRunToSrs(id: string, connectionId: string) {
  return request<{ detail: QraModelRunDetail; connection: SrsConnection; event: SrsSyncEvent }>(`/api/srs/model-runs/${encodeURIComponent(id)}/push`, {
    method: "POST",
    body: JSON.stringify({ connectionId }),
  });
}

export async function pollQraModelRunFromSrs(id: string, connectionId: string) {
  return request<{ detail: QraModelRunDetail; connection: SrsConnection; event: SrsSyncEvent }>(`/api/srs/model-runs/${encodeURIComponent(id)}/poll`, {
    method: "POST",
    body: JSON.stringify({ connectionId }),
  });
}

export async function loadControls() {
  return request<{ items: Control[] }>("/api/controls");
}

export async function createControl(input: ControlInput) {
  return request<{ item: Control }>("/api/controls", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateControl(id: string, input: ControlInput) {
  return request<{ item: Control }>(`/api/controls/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function deactivateControl(id: string) {
  return request<{ item: Control }>(`/api/controls/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export async function loadRiskControls(riskId = "") {
  const query = riskId ? `?riskId=${encodeURIComponent(riskId)}` : "";
  return request<{ items: RiskControl[] }>(`/api/controls/risk-controls${query}`);
}

export async function createRiskControl(input: RiskControlInput) {
  return request<{ item: RiskControl }>("/api/controls/risk-controls", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateRiskControl(id: string, input: RiskControlInput) {
  return request<{ item: RiskControl }>(`/api/controls/risk-controls/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function deleteRiskControl(id: string) {
  return request<{ ok: true }>(`/api/controls/risk-controls/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export async function loadAssuranceTests() {
  return request<{ items: AssuranceTest[] }>("/api/controls/assurance-tests");
}

export async function createAssuranceTest(input: AssuranceTestInput) {
  return request<{ item: AssuranceTest }>("/api/controls/assurance-tests", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateAssuranceTest(id: string, input: AssuranceTestInput) {
  return request<{ item: AssuranceTest }>(`/api/controls/assurance-tests/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function deleteAssuranceTest(id: string) {
  return request<{ ok: true }>(`/api/controls/assurance-tests/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export async function loadControlObligations() {
  return request<{ items: ControlObligation[] }>("/api/controls/obligations");
}

export async function createControlObligation(input: ControlObligationInput) {
  return request<{ item: ControlObligation }>("/api/controls/obligations", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateControlObligation(id: string, input: ControlObligationInput) {
  return request<{ item: ControlObligation }>(`/api/controls/obligations/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function deleteControlObligation(id: string) {
  return request<{ ok: true }>(`/api/controls/obligations/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export async function loadSamplingPlans() {
  return request<{ items: SamplingPlan[] }>("/api/controls/sampling-plans");
}
export async function createSamplingPlan(input: SamplingPlanInput) {
  return request<{ item: SamplingPlan }>("/api/controls/sampling-plans", { method: "POST", body: JSON.stringify(input) });
}
export async function updateSamplingPlan(id: string, input: SamplingPlanInput) {
  return request<{ item: SamplingPlan }>(`/api/controls/sampling-plans/${encodeURIComponent(id)}`, { method: "PUT", body: JSON.stringify(input) });
}
export async function deleteSamplingPlan(id: string) {
  return request<{ ok: true }>(`/api/controls/sampling-plans/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export async function loadObligationEscalations() {
  return request<{ items: ObligationEscalation[] }>("/api/controls/escalations");
}
export async function raiseObligationEscalation(input: ObligationEscalationInput) {
  return request<{ item: ObligationEscalation }>("/api/controls/escalations", { method: "POST", body: JSON.stringify(input) });
}
export async function updateObligationEscalation(id: string, input: ObligationEscalationInput) {
  return request<{ item: ObligationEscalation }>(`/api/controls/escalations/${encodeURIComponent(id)}`, { method: "PUT", body: JSON.stringify(input) });
}

export async function loadTaxonomyItems(type = "") {
  const query = type ? `?type=${encodeURIComponent(type)}` : "";
  return request<{ items: TaxonomyItem[] }>(`/api/structure/taxonomy-items${query}`);
}

export async function createTaxonomyItem(input: TaxonomyItemInput) {
  return request<{ item: TaxonomyItem }>("/api/structure/taxonomy-items", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateTaxonomyItem(id: string, input: TaxonomyItemInput) {
  return request<{ item: TaxonomyItem }>(`/api/structure/taxonomy-items/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function deactivateTaxonomyItem(id: string) {
  return request<{ item: TaxonomyItem }>(`/api/structure/taxonomy-items/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export async function importTaxonomyItems(items: TaxonomyItemInput[]) {
  return request<{ imported: TaxonomyItem[]; items: TaxonomyItem[] }>("/api/structure/taxonomy-import", {
    method: "POST",
    body: JSON.stringify({ items }),
  });
}
